import React from "react";
import { formatDelayMs } from "../ResourceManager/Topology/utils/latencyMatrix";
import { LatencySite } from "./request";
import { DirectedSiteLatency, shortSiteLabel } from "./siteLatencyLookup";

export interface SitePair {
    row: LatencySite;
    col: LatencySite;
    latency: DirectedSiteLatency;
}

interface Props {
    /** The hovered or selected pair; null shows the extremes instead. */
    active: SitePair | null;
    closest: SitePair | null;
    farthest: SitePair | null;
    /** On a phone the pairs are a list to tap, with nothing to hover (CTX-1382). */
    isTouchList?: boolean;
}

function pairName(pair: SitePair): string {
    return `${shortSiteLabel(pair.row.label)} ↔ ${shortSiteLabel(pair.col.label)}`;
}

/** Label above, value below: reads as one row of figures across the strip. */
const Figure: React.FC<{
    label: string;
    value: string;
    emphasised?: boolean;
}> = ({ label, value, emphasised = false }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-xs text-semantic-text-secondary">{label}</span>
        <span
            className={`tabular-nums text-semantic-text-primary ${
                emphasised ? "text-xl font-bold" : "text-sm font-semibold"
            }`}
        >
            {value}
        </span>
    </div>
);

/** Both directions of one pair: the round trip, then each leg. */
const PairDetail: React.FC<{ pair: SitePair }> = ({ pair }) => {
    const from = shortSiteLabel(pair.row.label);
    const to = shortSiteLabel(pair.col.label);
    return (
        <>
            <Figure label="Sites" value={pairName(pair)} />
            <Figure
                label="Round trip"
                value={`${formatDelayMs(pair.latency.rttUs)} ms`}
                emphasised
            />
            <Figure
                label={`${from} → ${to}`}
                value={`${formatDelayMs(pair.latency.outboundUs)} ms`}
            />
            <Figure
                label={`${to} → ${from}`}
                value={`${formatDelayMs(pair.latency.inboundUs)} ms`}
            />
        </>
    );
};

/**
 * A strip above the per-site table: the hovered or selected pair in detail,
 * or — before anything is picked — the closest and farthest pair overall, so
 * it says something useful from the first paint.
 */
const SiteLatencyPanel: React.FC<Props> = ({ active, closest, farthest, isTouchList = false }) => (
    <div
        className="flex min-h-[4.5rem] flex-wrap items-center gap-x-10 gap-y-3 rounded-lg border border-semantic-border-secondary bg-semantic-bg-secondary px-4 py-3"
        aria-live="polite"
    >
        {active ? (
            <PairDetail pair={active} />
        ) : (
            <>
                {closest && (
                    <Figure
                        label="Closest sites"
                        value={`${pairName(closest)} · ${formatDelayMs(closest.latency.rttUs)} ms`}
                    />
                )}
                {farthest && (
                    <Figure
                        label="Farthest sites"
                        value={`${pairName(farthest)} · ${formatDelayMs(farthest.latency.rttUs)} ms`}
                    />
                )}
                <span className="text-xs text-semantic-text-secondary">
                    {isTouchList
                        ? "Tap a pair to see both directions."
                        : "Hover a cell to preview a pair; click it to keep it selected."}
                </span>
            </>
        )}
    </div>
);

export default SiteLatencyPanel;
